import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import {
  ArrowLeft,
  ArrowRight,
  Recycle,
  Wallet,
  Leaf,
  RefreshCw,
  ShieldCheck,
  ClipboardCheck,
  ClipboardList,
  Smartphone,
  Tablet,
  Monitor,
  CheckCircle2,
  AlertTriangle,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";

import hardwareInruilenHeroBg from "@/assets/hardware-inruilen-hero-bg.jpg";

const stappen = [
  {
    nummer: "1",
    titel: "Hardware aanschaffen",
    tekst: "Je koopt nieuwe smartphones, tablets of computers via Rinqel.",
  },
  {
    nummer: "2",
    titel: "Inruilwaarde afspreken",
    tekst: "Vooraf spreken we een gegarandeerde inruilwaarde af per device — vastgelegd en transparant.",
  },
  {
    nummer: "3",
    titel: "Device inleveren",
    tekst: "Aan het einde van de looptijd levert de medewerker het oude toestel in. Zonder extra administratie.",
  },
  {
    nummer: "4",
    titel: "Beoordeling",
    tekst: "We beoordelen elk device volgens ons transparante gradingmodel — eerlijk en marktconform.",
  },
  {
    nummer: "5",
    titel: "Verrekening",
    tekst: "Het afgesproken bedrag wordt verrekend op je nieuwe hardwarebestelling. Klaar.",
  },
];

const voordelen = [
  {
    icon: Wallet,
    titel: "Bespaar op toekomstige hardware",
    tekst: "Je weet vóór aanschaf al wat je terugkrijgt. Het inruilbedrag wordt automatisch verrekend bij je volgende bestelling — geen verrassingen.",
  },
  {
    icon: Leaf,
    titel: "Beter voor het milieu",
    tekst: "Hergebruik voorkomt dat bruikbare apparaten op de afvalberg belanden. Grondstoffen worden bespaard en CO₂-uitstoot verminderd.",
  },
  {
    icon: RefreshCw,
    titel: "Een tweede leven voor je devices",
    tekst: "Ingeruilde toestellen worden vakkundig beoordeeld, gerepareerd en opnieuw ingezet — in onderwijs, non-profit of de refurbished markt.",
  },
  {
    icon: ShieldCheck,
    titel: "Geen risico op datalekken",
    tekst: "Alle devices worden veilig en gecertificeerd gewist. Er wordt gecontroleerd op FMiP, Google Lock, iCloud lock, DEP/Knox en pincode/pattern lock. Je ontvangt altijd een datawiping-certificaat.",
  },
  {
    icon: ClipboardCheck,
    titel: "Transparant beoordelingsmodel",
    tekst: "We werken met een helder gradingmodel zodat je precies weet hoe de inruilwaarde wordt bepaald. Eerlijk en voorspelbaar.",
  },
  {
    icon: ClipboardList,
    titel: "Eenvoudig hardwarebeheer",
    tekst: "Geen losse Excel-lijsten of vergeten toestellen in laden. Wij houden bij welke devices in omloop zijn, wanneer ze ingeruild worden en wat de restwaarde is.",
  },
];

const grading = [
  {
    icon: CheckCircle2,
    label: "Werkend & in goede staat",
    kleur: "text-rinqel-green",
    bgKleur: "bg-rinqel-green/10",
    borderKleur: "border-rinqel-green/30",
    waarde: "Hoogste inruilwaarde",
    punten: [
      "Functioneel volledig werkend",
      "Lichte gebruikssporen toegestaan",
      "Geen barsten of grote schade",
    ],
  },
  {
    icon: AlertTriangle,
    label: "Beschadigd, maar bruikbaar",
    kleur: "text-yellow-600",
    bgKleur: "bg-yellow-50",
    borderKleur: "border-yellow-300",
    waarde: "Nette inruilwaarde",
    punten: [
      "Apparaat gaat aan en is oplaadbaar",
      "Scherm mag barsten bevatten",
      "Functies kunnen deels defect zijn",
    ],
  },
  {
    icon: XCircle,
    label: "Defect",
    kleur: "text-destructive",
    bgKleur: "bg-destructive/5",
    borderKleur: "border-destructive/20",
    waarde: "Circulair verwerkt",
    punten: [
      "Gaat niet meer aan",
      "Display werkt niet",
      "Zware schade of verbogen device",
    ],
  },
];

const devices = [
  { icon: Smartphone, label: "Smartphones" },
  { icon: Tablet, label: "Tablets" },
  { icon: Monitor, label: "Computers" },
];

const HardwareInruilen = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[480px] overflow-hidden">
        <img
          src={hardwareInruilenHeroBg}
          alt="Medewerker levert een oud device in voor inruil bij Rinqel"
          loading="eager" decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/25" />
        <div className="container relative z-10 flex min-h-[480px] items-center py-20">
          <div className="max-w-2xl">
            <Link
              to="/diensten/hardware"
              className="mb-6 inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Terug naar Hardware
            </Link>

            <motion.div
              className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.05 }}
            >
              <Recycle className="h-7 w-7 text-white" />
            </motion.div>

            <motion.h1
              className="text-4xl font-bold leading-[1.1] tracking-tight text-white md:text-5xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Ruil je oude devices{" "}
              <span className="text-gradient">in</span>
            </motion.h1>

            <motion.p
              className="mt-5 max-w-xl text-lg leading-relaxed text-white/80"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Bij Rinqel spreken we vooraf een gegarandeerde inruilwaarde af voor je oude smartphones, tablets en computers.
              Aan het einde van de looptijd verrekenen we dat bedrag op je nieuwe hardware. Simpel.
            </motion.p>

            <motion.div
              className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/70"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Vooraf gegarandeerde waarde
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-rinqel-blue" />
                Gecertificeerde datawiping
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-rinqel-green" />
                Duurzaam & circulair
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Hoe werkt het */}
      <section className="py-16 md:py-24">
        <div className="container">
          <motion.div
            className="mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Hoe werkt het <span className="text-gradient">inruilen?</span>
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
              In vijf duidelijke stappen — zonder ingewikkelde contracten of verborgen kosten.
            </p>
          </motion.div>

          <div className="relative max-w-3xl mx-auto">
            {stappen.map((stap, i) => (
              <motion.div
                key={stap.nummer}
                className="relative flex gap-6 pb-10 last:pb-0"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                {/* Verticale lijn */}
                {i < stappen.length - 1 && (
                  <div className="absolute left-5 top-12 bottom-0 w-px bg-border" />
                )}
                {/* Nummer */}
                <div className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold text-sm">
                  {stap.nummer}
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{stap.titel}</h3>
                  <p className="mt-1 text-muted-foreground">{stap.tekst}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Voordelen */}
      <section className="bg-muted/40 py-16 md:py-24">
        <div className="container">
          <motion.div
            className="mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Waarom inruilen bij <span className="text-gradient">Rinqel?</span>
            </h2>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {voordelen.map((v, i) => (
              <motion.div
                key={v.titel}
                className="rounded-xl border bg-card p-6 shadow-[var(--shadow-card)] hover:shadow-[var(--shadow-card-hover)] transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                  <v.icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">{v.titel}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.tekst}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gradingmodel */}
      <section className="py-16 md:py-24">
        <div className="container">
          <motion.div
            className="mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Transparant <span className="text-gradient">gradingmodel</span>
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
              We beoordelen elk ingeleverd device volgens drie duidelijke classificaties.
            </p>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-3 max-w-4xl mx-auto">
            {grading.map((g, i) => (
              <motion.div
                key={g.label}
                className={`rounded-xl border ${g.borderKleur} ${g.bgKleur} p-6`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <g.icon className={`h-6 w-6 ${g.kleur}`} />
                  <h3 className="font-semibold">{g.label}</h3>
                </div>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {g.punten.map((punt) => (
                    <li key={punt} className="flex items-start gap-2">
                      <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${g.kleur.replace("text-", "bg-")}`} />
                      {punt}
                    </li>
                  ))}
                </ul>
                <div className={`mt-4 text-xs font-semibold ${g.kleur}`}>
                  → {g.waarde}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Voor welke devices */}
      <section className="bg-muted/40 py-16 md:py-24">
        <div className="container">
          <motion.div
            className="mb-10 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Voor welke <span className="text-gradient">devices?</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-3 gap-6 max-w-lg mx-auto">
            {devices.map((d, i) => (
              <motion.div
                key={d.label}
                className="flex flex-col items-center gap-3 rounded-xl border bg-card p-6 shadow-[var(--shadow-card)]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                  <d.icon className="h-6 w-6 text-primary" />
                </div>
                <span className="font-semibold text-sm">{d.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Wat we NIET doen */}
      <section className="py-12">
        <div className="container">
          <motion.div
            className="mx-auto max-w-2xl rounded-xl border border-dashed border-border bg-muted/30 p-6 text-center"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Helder & simpel.</strong>{" "}
              Wij bieden geen leaseconstructies, geen vooruitbetalingen en geen tussentijdse uitbetalingen.
              Gewoon een eerlijke inruilwaarde die verrekend wordt bij je volgende hardware-aankoop.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-muted/40 py-16 md:py-24">
        <div className="container text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto max-w-2xl"
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Benieuwd wat jouw devices{" "}
              <span className="text-gradient">waard zijn?</span>
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Neem contact op en we bespreken samen de mogelijkheden voor jouw organisatie.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Button size="lg" asChild>
                <TrackedLink to="/contact" trackLabel="Neem contact op" trackLocation="hardware-inruilen-cta">
                  Neem contact op
                  <ArrowRight className="ml-2 h-4 w-4" />
                </TrackedLink>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/diensten/hardware">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Terug naar Hardware
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default HardwareInruilen;
